import { WhereFilterOp } from "firebase/firestore";

import {
  FirestoreCollectionProsessorOptions,
  createFirestoreCollectionProsessor,
} from "../firebase/firestore";

import "./SheetDataRow";
import "./SheetFieldSchema";
import "./ReferredFrom";
import { SheetRowsIOQuery } from "@/sheetRowsIO/query";
import {
  TadTimeRange,
  TadTimeRangeRaw,
  encodeTadTimeRange,
  parseTadTimeRange,
} from "./TadTimeRange";

export const sheetOptimizeTypeList = [
  "both",
  "output_only",
  "input_only",
  "input_params",
  "input_weight_raw",
  "result_score_summaries",
  "result_alert_details",
  "report",
  "none",
  null,
] as const;

export const sheetOptimizeTypeGroupList = [
  "main",
  "output",
  "input",
  "params",
  "results",
  "others",
] as const;

export const sheetOpenAPITypeList = ["object", "array"] as const;
export const sheetAlgorithmExcelSheetTypeList = [
  "dataframe",
  "keyvalue",
] as const;

type SheetOptimizeTypes = (typeof sheetOptimizeTypeList)[number];
export type SheetOptimizeTypesGroup =
  (typeof sheetOptimizeTypeGroupList)[number];
type SheetOpenAPIType = (typeof sheetOpenAPITypeList)[number];
type SheetAlgorithmExcelSheetType =
  (typeof sheetAlgorithmExcelSheetTypeList)[number];

export const sheetOptimizeTypeMap = new Map<
  SheetOptimizeTypesGroup,
  (typeof sheetOptimizeTypeList)[number][]
>([
  ["main", ["both"]],
  ["input", ["input_only"]],
  ["output", ["output_only"]],
  ["params", ["input_params", "input_weight_raw"]],
  ["results", ["result_score_summaries", "result_alert_details", "report"]],
  ["others", ["none", null]],
]);

/**
 * TADS参照シートで指定できる最適化利用タイプ。ADR (6) のとおり `both | input_only` のみ。
 * `both` はジョブからの書き戻し対象にもなる。
 */
export const tadReferenceSheetOptimizeTypeList = [
  "input_only",
  "both",
] as const satisfies readonly SheetOptimizeTypes[];

export type TadReferenceSheetOptimizeType =
  (typeof tadReferenceSheetOptimizeTypeList)[number];

export const isTadReferenceSheetOptimizeType = (
  value: unknown
): value is TadReferenceSheetOptimizeType =>
  tadReferenceSheetOptimizeTypeList.some((v) => v === value);

export type SheetTadReferTo = {
  tadSheetId: string;
  /** TADS から切り取る時間範囲(相対式)。解決は `domain/TadTimeRange.ts` を参照。 */
  timeRange: TadTimeRange;
};

export type Sheet = {
  id: string;
  displayName: string;
  name: string;
  firstRowId: string | null;
  algorithmExcelDataStartRow: string | null;
  algorithmExcelHeaderRow: string | null;
  algorithmExcelSheetType: SheetAlgorithmExcelSheetType | null;
  createdAt: string;
  updatedAt: string;
  description: string | null;
  isHidden: boolean;
  primaryKeyFieldIds: string[];
  sheetOptimizeType: SheetOptimizeTypes;
  openapiType: SheetOpenAPIType | null;
  sort: number;
  isSheetQueryOn: boolean;
  sheetQuerySheetFieldSchemaId: string;
  sheetQueryParameters: {
    value: unknown;
    opStr: WhereFilterOp;
  }[];
  isSheetQueryV2Enabled: boolean;
  sheetQueryV2Queries: SheetRowsIOQuery[];
  referTo: {
    versionId: string;
    sheetId: string;
  } | null;
  /** NOTE: TADS参照シートは自前の `sheet_field_schemas` を持たない。 */
  tadReferTo: SheetTadReferTo | null;
  lastTransactionId?: string;
  backend: "lds" | "firestore";
};

export const isTadReferenceSheet = (sheet: Sheet | null): boolean =>
  !!sheet?.tadReferTo;
type SheetRaw = {
  id: string;
  display_name: string;
  name: string;
  first_row_id: string | null;
  algorithm_excel_data_start_row: string | null;
  algorithm_excel_header_row: string | null;
  algorithm_excel_sheet_type: SheetAlgorithmExcelSheetType | null;
  created_at: string;
  updated_at: string;
  description: string | null;
  is_hidden: boolean;
  primary_key_field_ids: string[];
  sheet_optimize_type: SheetOptimizeTypes;
  openapi_type: SheetOpenAPIType | null;
  sort: number;
  sheet_query_on: boolean;
  sheet_query_sheet_field_schema_id: string;
  sheet_query_parameters: {
    value: unknown;
    op_str: WhereFilterOp;
  }[];
  is_sheet_query_v2_enabled?: boolean;
  sheet_query_v2_queries?: SheetRowsIOQuery[];
  refer_to: {
    version_id: string;
    sheet_id: string;
  } | null;
  // 既存のシートドキュメントには書かれていないため optional
  tad_refer_to?: {
    tad_sheet_id: string;
    time_range: TadTimeRangeRaw;
  } | null;
  last_transaction_id?: string;
  backend?: "lds" | "firestore";
};

const processorOptions: FirestoreCollectionProsessorOptions<
  Sheet,
  SheetRaw,
  Record<string, string>
> = {
  getCollectionPath: ({ organizationId, versionId }) =>
    `/app/spreadsheet_ui/v/1/organizations/${organizationId}/versions/${versionId}/sheets`,
  toFirestore: ({
    id,
    displayName,
    name,
    firstRowId,
    algorithmExcelDataStartRow,
    algorithmExcelHeaderRow,
    algorithmExcelSheetType,
    createdAt,
    updatedAt,
    description,
    isHidden,
    primaryKeyFieldIds,
    sheetOptimizeType,
    openapiType,
    sort,
    isSheetQueryOn,
    sheetQuerySheetFieldSchemaId,
    sheetQueryParameters,
    isSheetQueryV2Enabled,
    sheetQueryV2Queries,
    referTo,
    tadReferTo,
    lastTransactionId,
    backend,
  }) => ({
    id,
    display_name: displayName,
    name,
    first_row_id: firstRowId,
    algorithm_excel_data_start_row: algorithmExcelDataStartRow,
    algorithm_excel_header_row: algorithmExcelHeaderRow,
    algorithm_excel_sheet_type: algorithmExcelSheetType,
    created_at: createdAt,
    updated_at: updatedAt,
    description,
    is_hidden: isHidden,
    primary_key_field_ids: primaryKeyFieldIds,
    sheet_optimize_type: sheetOptimizeType,
    openapi_type: openapiType,
    sort,
    sheet_query_on: isSheetQueryOn,
    sheet_query_sheet_field_schema_id: sheetQuerySheetFieldSchemaId,
    sheet_query_parameters: sheetQueryParameters.map(
      ({ value, opStr: op_str }) => ({ value, op_str })
    ),
    is_sheet_query_v2_enabled: isSheetQueryV2Enabled,
    sheet_query_v2_queries: sheetQueryV2Queries,
    refer_to: referTo
      ? {
          version_id: referTo.versionId,
          sheet_id: referTo.sheetId,
        }
      : null,
    ...(tadReferTo
      ? {
          tad_refer_to: {
            tad_sheet_id: tadReferTo.tadSheetId,
            time_range: encodeTadTimeRange(tadReferTo.timeRange),
          },
        }
      : {}),
    ...(lastTransactionId ? { last_transaction_id: lastTransactionId } : {}),
    ...(backend ? { backend } : {}),
  }),
  fromFirestore: ({
    id,
    display_name,
    name,
    first_row_id,
    algorithm_excel_data_start_row,
    algorithm_excel_header_row,
    algorithm_excel_sheet_type,
    created_at,
    updated_at,
    description,
    is_hidden,
    primary_key_field_ids,
    sheet_optimize_type,
    openapi_type,
    sort,
    sheet_query_on,
    sheet_query_sheet_field_schema_id,
    sheet_query_parameters,
    is_sheet_query_v2_enabled,
    sheet_query_v2_queries,
    refer_to,
    tad_refer_to,
    last_transaction_id,
    backend,
  }) => ({
    id,
    displayName: display_name,
    name,
    firstRowId: first_row_id,
    algorithmExcelDataStartRow: algorithm_excel_data_start_row,
    algorithmExcelHeaderRow: algorithm_excel_header_row,
    algorithmExcelSheetType: algorithm_excel_sheet_type,
    createdAt: created_at,
    updatedAt: updated_at,
    description,
    isHidden: is_hidden ?? false,
    primaryKeyFieldIds: primary_key_field_ids,
    sheetOptimizeType: sheet_optimize_type,
    openapiType: openapi_type,
    sort,
    isSheetQueryOn: sheet_query_on,
    sheetQuerySheetFieldSchemaId: sheet_query_sheet_field_schema_id,
    sheetQueryParameters: sheet_query_parameters.map(
      ({ value, op_str: opStr }) => ({ value, opStr })
    ),
    isSheetQueryV2Enabled: is_sheet_query_v2_enabled ?? false,
    sheetQueryV2Queries: sheet_query_v2_queries ?? [],
    referTo: refer_to
      ? {
          versionId: refer_to.version_id,
          sheetId: refer_to.sheet_id,
        }
      : null,
    tadReferTo: tad_refer_to
      ? {
          tadSheetId: tad_refer_to.tad_sheet_id,
          timeRange: parseTadTimeRange(tad_refer_to.time_range),
        }
      : null,
    ...(last_transaction_id ? { lastTransactionId: last_transaction_id } : {}),
    backend: backend ?? "firestore",
  }),
};

export const {
  useGetAll: useGetSheets,
  useGet: useGetSheet,
  useSet: useSetSheet,
  useUpdate: useUpdateSheet,
  useRecursiveDelete: useRecursiveDeleteSheet,
  useItem: useSheet,
  useItemsQuery: useSheetsQuery,
} = createFirestoreCollectionProsessor(processorOptions);
